import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useTaxonomyFeedback } from './useSectorTaxonomy';
import { Pager } from '../../components/Pager';
import {
  ALL_FEEDBACK_KINDS,
  ALL_FEEDBACK_SCOPES,
  ALL_FEEDBACK_SEVERITIES,
  buildDefaultFeedbackFilters,
  buildTargetRefLink,
  feedbackFiltersFromSearchParams,
  feedbackFiltersToQuery,
  feedbackFiltersToSearchParams,
  formatFeedbackKind,
  formatFeedbackScope,
  formatFeedbackSeverity,
  targetRefLinkToHref,
  type FeedbackFilters,
} from './feedback.types';
import type {
  TaxonomyFeedbackItem,
  TaxonomyFeedbackKind,
  TaxonomyFeedbackScope,
  TaxonomyFeedbackSeverity,
} from '../../api/sectorTaxonomyApi';
import { InfoTooltip } from '../../components/InfoTooltip';
import { TX_TOOLTIPS } from './tooltipTexts';

// 권위: Type-A BootstrapProposal v2.0 `feedback[]` 채널 + ARCH_DASHBOARD.md §4.11 피드백 탭.
//
// 상단 요약 카드 · 필터 바 · 테이블 · 페이지네이션.
// URL 쿼리스트링 = 단일 진실원 (북마크/공유 가능). 탭 쿼리 `tab` 는 부모가 관리.

// SectorTaxonomyPage 탭 컨텍스트 안에서 URL 의 `tab` 값은 유지해야 함.
// 본 탭 전용 필터만 이 모듈이 관리하며, 다른 쿼리 키는 pass-through.
const RESERVED_PARAMS = new Set(['tab']);

function mergeFilterParams(
  existing: URLSearchParams,
  filterParams: URLSearchParams
): URLSearchParams {
  const next = new URLSearchParams();
  // 필터 바깥 쿼리 (예: tab) 는 그대로 유지.
  existing.forEach((value, key) => {
    if (RESERVED_PARAMS.has(key)) next.set(key, value);
  });
  filterParams.forEach((value, key) => next.set(key, value));
  return next;
}

function formatKstTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

function shortRunId(runId: string): string {
  if (!runId) return '—';
  // UUID 8자리 prefix.
  return runId.length > 8 ? `${runId.slice(0, 8)}…` : runId;
}

function severityClass(sev: string): string {
  switch (sev) {
    case 'blocker':
      return 'tx-fb-sev-blocker';
    case 'suggestion':
      return 'tx-fb-sev-suggestion';
    default:
      return 'tx-fb-sev-info';
  }
}

function rowClass(sev: string): string {
  return sev === 'blocker' ? 'tx-fb-row--blocker' : '';
}

export function FeedbackTab(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // 인라인 확장 row — 단일 expand (한 번에 한 개만 열림).
  // key = `${runId}-${feedbackIndex}` (테이블 row key 와 동일).
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const filters = useMemo<FeedbackFilters>(
    () => feedbackFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  const setFilters = useCallback(
    (next: FeedbackFilters) => {
      const filterSp = feedbackFiltersToSearchParams(next);
      const merged = mergeFilterParams(searchParams, filterSp);
      setSearchParams(merged, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onReset = useCallback(() => {
    const filterSp = feedbackFiltersToSearchParams(buildDefaultFeedbackFilters());
    const merged = mergeFilterParams(searchParams, filterSp);
    setSearchParams(merged, { replace: true });
  }, [searchParams, setSearchParams]);

  const query = useTaxonomyFeedback(feedbackFiltersToQuery(filters));
  const items = query.data?.items ?? [];
  const runs = query.data?.runs ?? [];
  const total = query.data?.total ?? 0;

  // 요약 카드 지표 — 현재 기간의 피드백 총합 · blocker · 가장 최근 run.
  const summary = useMemo(() => {
    const totalFeedback = total;
    const blockerCount = runs.reduce((acc, r) => acc + (r.blockerCount ?? 0), 0);
    // runs 는 서버가 completedAt desc 로 정렬해 줄 것이라 가정하되, 방어적으로 max 추출.
    let latestAt: string | null = null;
    for (const r of runs) {
      if (!r.completedAt) continue;
      if (latestAt === null || r.completedAt > latestAt) latestAt = r.completedAt;
    }
    return { totalFeedback, blockerCount, latestAt };
  }, [total, runs]);

  const toggleKind = useCallback(
    (k: TaxonomyFeedbackKind) => {
      const next = filters.kind.includes(k)
        ? filters.kind.filter((x) => x !== k)
        : [...filters.kind, k];
      setFilters({ ...filters, kind: next, page: 0 });
    },
    [filters, setFilters]
  );

  const toggleSeverity = useCallback(
    (s: TaxonomyFeedbackSeverity) => {
      const next = filters.severity.includes(s)
        ? filters.severity.filter((x) => x !== s)
        : [...filters.severity, s];
      // blockerOnly 와 severity 필터는 상호 배타: blockerOnly 는 severity=['blocker'] 로 치환된다.
      // 운영자가 severity 를 직접 건드리면 blockerOnly 해제.
      setFilters({ ...filters, severity: next, blockerOnly: false, page: 0 });
    },
    [filters, setFilters]
  );

  const toggleScope = useCallback(
    (s: TaxonomyFeedbackScope) => {
      const next = filters.scope.includes(s)
        ? filters.scope.filter((x) => x !== s)
        : [...filters.scope, s];
      setFilters({ ...filters, scope: next, page: 0 });
    },
    [filters, setFilters]
  );

  const toggleBlockerOnly = useCallback(() => {
    setFilters({
      ...filters,
      blockerOnly: !filters.blockerOnly,
      severity: [],
      page: 0,
    });
  }, [filters, setFilters]);

  const renderTargetRef = useCallback((ref: string | null) => {
    if (!ref) return <span className="pca-muted">—</span>;
    const link = buildTargetRefLink(ref);
    if (link) {
      return (
        <Link className="tx-fb-target" to={targetRefLinkToHref(link)}>
          <code>{ref}</code>
        </Link>
      );
    }
    return <code className="tx-fb-target-inert">{ref}</code>;
  }, []);

  return (
    <div>
      {/* 요약 카드 */}
      <div className="tx-fb-summary" role="status" aria-live="polite">
        <div className="tx-fb-summary__metric">
          <span className="tx-banner__label">기간 내 피드백</span>
          <span className="tx-banner__value" data-testid="tx-fb-total-count">
            {summary.totalFeedback.toLocaleString('ko-KR')} 건
          </span>
        </div>
        <div className="tx-fb-summary__metric">
          <span className="tx-banner__label">blocker</span>
          <span
            className={
              'tx-banner__value ' +
              (summary.blockerCount > 0 ? 'tx-fb-summary__blocker' : '')
            }
            data-testid="tx-fb-blocker-count"
          >
            {summary.blockerCount.toLocaleString('ko-KR')} 건
          </span>
        </div>
        <div className="tx-fb-summary__metric">
          <span className="tx-banner__label">최근 run</span>
          <span className="tx-banner__value tx-fb-summary__latest">
            {summary.latestAt ? formatKstTs(summary.latestAt) : '—'}
          </span>
        </div>
      </div>

      {/* 필터 바 */}
      <div className="pca-filter" style={{ marginTop: 12 }}>
        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="fb-from">
            from
          </label>
          <input
            id="fb-from"
            type="date"
            className="pca-input"
            value={filters.from}
            onChange={(e) =>
              setFilters({ ...filters, from: e.target.value, page: 0 })
            }
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="fb-to">
            to
          </label>
          <input
            id="fb-to"
            type="date"
            className="pca-input"
            value={filters.to}
            onChange={(e) =>
              setFilters({ ...filters, to: e.target.value, page: 0 })
            }
          />
        </div>
        <div className="pca-filter__group">
          <label className="pca-filter__label" htmlFor="fb-run">
            run
          </label>
          <select
            id="fb-run"
            className="pca-select"
            value={filters.runId}
            onChange={(e) =>
              setFilters({ ...filters, runId: e.target.value, page: 0 })
            }
          >
            <option value="">전체</option>
            {runs
              .filter((r): r is typeof r & { runId: string } => !!r.runId)
              .map((r) => (
                <option key={r.runId} value={r.runId}>
                  {shortRunId(r.runId)} · {formatKstTs(r.completedAt)} ·
                  {' '}
                  피드백 {r.feedbackCount}
                  {r.blockerCount > 0 ? ` · blocker ${r.blockerCount}` : ''}
                </option>
              ))}
          </select>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">
            kind
            <InfoTooltip label={TX_TOOLTIPS.fbKind} testId="tx-tip-fb-kind" />
          </label>
          <div className="pca-chip-row">
            {ALL_FEEDBACK_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                className={
                  'pca-chip' +
                  (filters.kind.includes(k) ? ' pca-chip--active' : '')
                }
                onClick={() => toggleKind(k)}
              >
                {formatFeedbackKind(k)}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">
            severity
            <InfoTooltip
              label={TX_TOOLTIPS.fbSeverity}
              testId="tx-tip-fb-severity"
            />
          </label>
          <div className="pca-chip-row">
            {ALL_FEEDBACK_SEVERITIES.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  'pca-chip tx-fb-chip--' +
                  s +
                  (filters.severity.includes(s) && !filters.blockerOnly
                    ? ' pca-chip--active'
                    : '')
                }
                onClick={() => toggleSeverity(s)}
                disabled={filters.blockerOnly}
              >
                {formatFeedbackSeverity(s)}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">
            scope
            <InfoTooltip label={TX_TOOLTIPS.fbScope} testId="tx-tip-fb-scope" />
          </label>
          <div className="pca-chip-row">
            {ALL_FEEDBACK_SCOPES.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  'pca-chip' +
                  (filters.scope.includes(s) ? ' pca-chip--active' : '')
                }
                onClick={() => toggleScope(s)}
              >
                {formatFeedbackScope(s)}
              </button>
            ))}
          </div>
        </div>

        <div className="pca-filter__group">
          <label className="pca-filter__label">빠른 필터</label>
          <label className="tx-fb-blocker-toggle">
            <input
              type="checkbox"
              checked={filters.blockerOnly}
              onChange={toggleBlockerOnly}
            />
            blocker 만
          </label>
        </div>

        <div className="pca-filter__group" style={{ marginLeft: 'auto' }}>
          <label className="pca-filter__label">&nbsp;</label>
          <button type="button" className="pca-btn" onClick={onReset}>
            <X size={14} strokeWidth={1.75} />
            초기화
          </button>
        </div>
      </div>

      {query.isError && (
        <div className="pca-warning-banner" style={{ marginTop: 8 }}>
          피드백 로드 실패: {(query.error as Error).message}
        </div>
      )}

      {/* 테이블 */}
      <div className="pca-table-wrap" style={{ marginTop: 12 }}>
        <table className="pca-table tx-fb-table">
          <thead>
            <tr>
              <th style={{ width: 32 }} aria-label="expand" />
              <th style={{ width: 160 }}>시각</th>
              <th style={{ width: 120 }}>run</th>
              <th style={{ width: 120 }}>kind</th>
              <th style={{ width: 110 }}>severity</th>
              <th style={{ width: 120 }}>scope</th>
              <th style={{ width: 220 }}>
                target
                <InfoTooltip
                  label={TX_TOOLTIPS.fbTarget}
                  testId="tx-tip-fb-target"
                />
              </th>
              <th>message</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={8} className="pca-muted">
                  로딩 중…
                </td>
              </tr>
            )}
            {!query.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="pca-muted tx-fb-empty">
                  아직 피드백이 없습니다. Type-A 를 실행하면 LLM 의 피드백이
                  누적됩니다.
                </td>
              </tr>
            )}
            {items.map((item) => {
              const rowKey = `${item.runId}-${item.feedbackIndex}`;
              const isExpanded = expandedKey === rowKey;
              return (
                <FeedbackRow
                  key={rowKey}
                  rowKey={rowKey}
                  item={item}
                  renderTargetRef={renderTargetRef}
                  isExpanded={isExpanded}
                  onToggle={() =>
                    setExpandedKey((prev) => (prev === rowKey ? null : rowKey))
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <Pager
        page={filters.page}
        pageSize={filters.pageSize}
        total={total}
        onPageChange={(p) => setFilters({ ...filters, page: p })}
      />
    </div>
  );
}

// 개별 행 — severity blocker 인 경우 좌측 빨강 바 + 강조.
// 우측 화살표 아이콘 클릭 시 아래 expand <tr> 에 message 전문 + target_ref 풀 링크 표시.
function FeedbackRow({
  rowKey,
  item,
  renderTargetRef,
  isExpanded,
  onToggle,
}: {
  rowKey: string;
  item: TaxonomyFeedbackItem;
  renderTargetRef: (ref: string | null) => JSX.Element;
  isExpanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <>
      <tr className={rowClass(item.severity)}>
        <td className="tx-fb-col-expand">
          <button
            type="button"
            className="tx-fb-expand-btn"
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-controls={`tx-fb-detail-${rowKey}`}
            aria-label={isExpanded ? '접기' : '펼치기'}
            data-testid={`tx-fb-expand-${rowKey}`}
          >
            {isExpanded ? (
              <ChevronDown size={14} strokeWidth={1.75} />
            ) : (
              <ChevronRight size={14} strokeWidth={1.75} />
            )}
          </button>
        </td>
        <td className="tx-fb-col-time">{formatKstTs(item.runCompletedAt)}</td>
        <td>
          <code title={item.runId}>{shortRunId(item.runId)}</code>
        </td>
        <td>
          <span className="tx-fb-kind">{formatFeedbackKind(item.kind)}</span>
        </td>
        <td>
          <span className={`tx-fb-sev ${severityClass(item.severity)}`}>
            {formatFeedbackSeverity(item.severity)}
          </span>
        </td>
        <td>
          <span className="pca-muted">{formatFeedbackScope(item.scope)}</span>
        </td>
        <td>{renderTargetRef(item.targetRef)}</td>
        <td className="tx-fb-col-message" title={item.message}>
          {item.message}
        </td>
      </tr>
      {isExpanded && (
        <tr
          className="tx-fb-row-detail"
          id={`tx-fb-detail-${rowKey}`}
          data-testid={`tx-fb-detail-${rowKey}`}
        >
          <td colSpan={8}>
            <div className="tx-fb-detail">
              <div className="tx-fb-detail__message">{item.message}</div>
              {item.targetRef && (
                <div className="tx-fb-detail__meta">
                  <span className="tx-fb-detail__label">target_ref</span>
                  {renderTargetRef(item.targetRef)}
                </div>
              )}
              <div className="tx-fb-detail__meta">
                <span className="tx-fb-detail__label">run</span>
                <code>{item.runId}</code>
                <span className="pca-muted">·</span>
                <span>{formatKstTs(item.runCompletedAt)}</span>
                {item.runTriggerBy && (
                  <>
                    <span className="pca-muted">·</span>
                    <span>{item.runTriggerBy}</span>
                  </>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
